import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Loader2 } from "lucide-react";
import {
  linkAvailabilityCalendar,
  listUserAvailabilityCalendars,
  previewAvailabilityCalendarEvents,
} from "../../../lib/data/availability";
import { AdvancedFieldsSection } from "./AdvancedFieldsSection.jsx";

// Phase 3b T0.5: reserved hues (FEASIBLE blue, PREFERRED green, amber/conflict,
// red/override, sky and lime adjacents) are intentionally excluded from this
// palette so linked-calendar colors never collide with vote-state signals.
export const LINKED_CALENDAR_PALETTE = Object.freeze([
  "#7c3aed", // violet-600
  "#ec4899", // pink-500
  "#a855f7", // purple-500
  "#0891b2", // cyan-600 (teal-ish, well-separated from blue-500)
  "#d946ef", // fuchsia-500
  "#f97316", // orange-500 (saturated; distinct from amber-500's warning role)
  "#64748b", // slate-500
  "#a16207", // yellow-700 / brown
]);

// Backwards-compat re-export — any consumer outside this file keeps working.
export const DEFAULT_COLORS = LINKED_CALENDAR_PALETTE;

const STEP_LOAD = "loading";
const STEP_PICK = "pick";
const STEP_CONFIGURE = "configure";
const STEP_PREVIEW = "preview";
const STEP_SUBMITTING = "submitting";
const STEP_NEEDS_AUTH = "needs-auth";

export default function LinkCalendarDialog({
  open,
  onOpenChange,
  alreadyLinkedIds = [],
  onLinked,
}) {
  const [step, setStep] = useState(STEP_LOAD);
  const [calendars, setCalendars] = useState([]);
  const [error, setError] = useState(null);
  const [picked, setPicked] = useState(null);

  // Configure-step form state
  const [calendarType, setCalendarType] = useState("conflict");
  const [color, setColor] = useState(LINKED_CALENDAR_PALETTE[0]);
  const [defaultVoteLevel, setDefaultVoteLevel] = useState("FEASIBLE");
  // Phase 3c-2 U4: advanced fields for free-type linked calendars
  // (leeway, fit-mode, group-scoping). Mirrors the schema fields the engine
  // already honors for marks; surfacing them here lets users author the
  // same intent on calendar imports.
  const [advanced, setAdvanced] = useState({
    leewayFrontMinutes: 0,
    leewayBackMinutes: 0,
    fitMode: "full-duration",
    appliesToQuestingGroupIds: null,
  });

  // Preview-step state
  const [previewItems, setPreviewItems] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      // Reset state on close
      setStep(STEP_LOAD);
      setCalendars([]);
      setError(null);
      setPicked(null);
      setCalendarType("conflict");
      setColor(LINKED_CALENDAR_PALETTE[0]);
      setDefaultVoteLevel("FEASIBLE");
      setAdvanced({
        leewayFrontMinutes: 0,
        leewayBackMinutes: 0,
        fitMode: "full-duration",
        appliesToQuestingGroupIds: null,
      });
      setPreviewItems([]);
      setPreviewLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const items = await listUserAvailabilityCalendars();
        if (cancelled) return;
        setCalendars(items);
        setStep(STEP_PICK);
      } catch (err) {
        if (cancelled) return;
        if (
          err?.code === "functions/failed-precondition" ||
          err?.message?.includes("not linked") ||
          err?.message?.includes("authorization expired")
        ) {
          setStep(STEP_NEEDS_AUTH);
        } else {
          setError(err?.message || "Failed to load calendars.");
          setStep(STEP_PICK);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  async function handleConfirm() {
    if (!picked) return;
    setError(null);
    setPreviewItems([]);
    setPreviewLoading(true);
    setStep(STEP_PREVIEW);
    try {
      const previewPayload = {
        calendarId: picked.id,
        calendarType,
      };
      if (calendarType === "free") {
        previewPayload.defaultVoteLevel = defaultVoteLevel;
      }
      const result = await previewAvailabilityCalendarEvents(previewPayload);
      setPreviewItems(result?.items || []);
    } catch (err) {
      setError(err?.message || "Failed to load preview.");
      setStep(STEP_CONFIGURE);
    } finally {
      setPreviewLoading(false);
    }
  }

  async function handleLink() {
    if (!picked) return;
    setStep(STEP_SUBMITTING);
    setError(null);
    try {
      const payload = {
        calendarId: picked.id,
        calendarType,
        color,
      };
      if (calendarType === "free") {
        payload.defaultVoteLevel = defaultVoteLevel;
        // Phase 3c-2 U4: only forward advanced fields for free-type — engine
        // ignores leeway/fit-mode for conflict-type, so saving them here
        // would just be noise.
        payload.leewayFrontMinutes = advanced.leewayFrontMinutes;
        payload.leewayBackMinutes = advanced.leewayBackMinutes;
        payload.fitMode = advanced.fitMode;
        payload.appliesToQuestingGroupIds = advanced.appliesToQuestingGroupIds;
      }
      await linkAvailabilityCalendar(payload);
      if (onLinked) onLinked(picked);
      onOpenChange(false);
    } catch (err) {
      setError(err?.message || "Failed to link calendar.");
      setStep(STEP_CONFIGURE);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Link a calendar</DialogTitle>
          <DialogDescription>
            Pick a Google calendar and choose how it should affect your
            availability.
          </DialogDescription>
        </DialogHeader>

        {step === STEP_LOAD && (
          <div className="flex items-center gap-3 py-8 text-sm text-slate-500 dark:text-slate-400">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Loading your calendars…
          </div>
        )}

        {step === STEP_NEEDS_AUTH && (
          <div className="space-y-3 py-4 text-sm">
            <p>
              Quest Scheduler needs read access to your Google calendars before
              you can link them here. Connect Google Calendar in Settings, then
              come back.
            </p>
            <a
              href="/settings"
              className="inline-flex items-center rounded-xl border border-slate-200 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              Open Settings
            </a>
          </div>
        )}

        {step === STEP_PICK && (
          <PickStep
            calendars={calendars}
            alreadyLinkedIds={alreadyLinkedIds}
            error={error}
            onPick={(item) => {
              setPicked(item);
              setStep(STEP_CONFIGURE);
            }}
          />
        )}

        {step === STEP_CONFIGURE && picked && (
          <ConfigureStep
            picked={picked}
            calendarType={calendarType}
            setCalendarType={setCalendarType}
            color={color}
            setColor={setColor}
            defaultVoteLevel={defaultVoteLevel}
            setDefaultVoteLevel={setDefaultVoteLevel}
            advanced={advanced}
            setAdvanced={setAdvanced}
            error={error}
            submitting={false}
            onBack={() => setStep(STEP_PICK)}
            onConfirm={handleConfirm}
          />
        )}

        {step === STEP_PREVIEW && picked && (
          <PreviewStep
            items={previewItems}
            loading={previewLoading}
            error={error}
            // Phase 4c P3: show the privacy-consent gate on first-link only.
            // Re-linkers have already consented once.
            requireConsent={alreadyLinkedIds.length === 0}
            onBack={() => {
              setPreviewItems([]);
              setStep(STEP_CONFIGURE);
            }}
            onLink={handleLink}
          />
        )}

        {step === STEP_SUBMITTING && picked && (
          <div className="flex items-center gap-3 py-8 text-sm text-slate-500 dark:text-slate-400">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Linking calendar…
          </div>
        )}

        {step === STEP_PICK || step === STEP_NEEDS_AUTH || step === STEP_LOAD ? (
          <DialogFooter>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              Cancel
            </button>
          </DialogFooter>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

const EFFECT_CHIP = {
  CONFLICT: {
    label: "Conflict",
    className:
      "bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-100",
  },
  "FREE-PREFERRED": {
    label: "Preferred",
    className:
      "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-100",
  },
  "FREE-FEASIBLE": {
    label: "Feasible",
    className:
      "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-100",
  },
};

function PreviewStep({
  items,
  loading,
  error,
  requireConsent = false,
  onBack,
  onLink,
}) {
  // First-link consent gate. Subsequent re-links skip this; the user has
  // already agreed to the policy at least once.
  const [consented, setConsented] = useState(!requireConsent);

  const linkDisabled = loading || (requireConsent && !consented);

  return (
    <div className="space-y-4 py-2 text-sm">
      <p className="font-medium text-slate-700 dark:text-slate-200">
        Preview — next 7 days
      </p>

      {loading && (
        <div className="flex items-center gap-3 py-6 text-slate-500 dark:text-slate-400">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Loading preview…
        </div>
      )}

      {!loading && items.length === 0 && (
        <p className="rounded-xl bg-slate-50 px-3 py-4 text-center text-slate-500 dark:bg-slate-800 dark:text-slate-400">
          No events in the next 7 days
        </p>
      )}

      {!loading && items.length > 0 && (
        <ul
          role="list"
          className="max-h-64 space-y-1 overflow-y-auto pr-1"
        >
          {items.map((item) => {
            const chip = EFFECT_CHIP[item.proposedEffect];
            return (
              <li
                key={item.id}
                className="flex items-center gap-3 rounded-xl border border-slate-200 px-3 py-2 dark:border-slate-700"
              >
                <span className="flex-1 truncate">
                  {item.summary || "(no title)"}
                </span>
                {chip && (
                  <span
                    className={`shrink-0 rounded px-2 py-0.5 text-xs font-medium ${chip.className}`}
                  >
                    {chip.label}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {error && (
        <p className="rounded-xl bg-rose-100 px-3 py-2 text-sm text-rose-700 dark:bg-rose-900/30 dark:text-rose-300">
          {error}
        </p>
      )}

      {requireConsent && (
        <label
          className="flex items-start gap-2 rounded-xl bg-slate-50 px-3 py-3 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300"
          data-testid="link-calendar-consent"
        >
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-slate-300 text-brand-primary focus-visible:ring-2 focus-visible:ring-ring"
            checked={consented}
            onChange={(e) => setConsented(e.target.checked)}
            data-testid="link-calendar-consent-checkbox"
          />
          <span>
            I have read the{" "}
            <a
              href="/privacy"
              target="_blank"
              rel="noreferrer"
              className="font-medium text-brand-primary underline hover:text-brand-primary/80 dark:text-blue-300 dark:hover:text-blue-200"
            >
              Privacy Policy
            </a>{" "}
            and understand that Quest Scheduler will cache structural event
            details from this calendar (start/end times, RSVP status) for
            conflict detection — never event titles, descriptions, or
            attendees. Titles I see in the app are fetched from my calendar
            provider on demand by my browser. I can delete the cache or
            unlink the calendar at any time.
          </span>
        </label>
      )}

      <DialogFooter>
        <button
          type="button"
          onClick={onBack}
          disabled={loading}
          className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          Back
        </button>
        <button
          type="button"
          onClick={onLink}
          disabled={linkDisabled}
          className="inline-flex items-center gap-2 rounded-xl bg-brand-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-primary/90 disabled:opacity-60"
        >
          Link calendar
        </button>
      </DialogFooter>
    </div>
  );
}

function PickStep({ calendars, alreadyLinkedIds, error, onPick }) {
  const linkedSet = new Set(alreadyLinkedIds);
  return (
    <div className="space-y-3">
      {error && (
        <p className="rounded-xl bg-rose-100 px-3 py-2 text-sm text-rose-700 dark:bg-rose-900/30 dark:text-rose-300">
          {error}
        </p>
      )}
      <ul role="list" className="max-h-72 space-y-1 overflow-y-auto pr-1">
        {calendars.length === 0 && (
          <li className="text-sm text-slate-500 dark:text-slate-400">
            No calendars found in your Google account.
          </li>
        )}
        {calendars.map((cal) => {
          const isLinked = linkedSet.has(cal.id);
          return (
            <li key={cal.id}>
              <button
                type="button"
                onClick={() => !isLinked && onPick(cal)}
                disabled={isLinked}
                className="flex w-full items-center gap-3 rounded-xl border border-slate-200 px-3 py-2 text-left text-sm transition-colors hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:hover:bg-slate-800"
              >
                <span
                  aria-hidden="true"
                  className="inline-block h-3 w-3 rounded-full"
                  style={{ backgroundColor: cal.backgroundColor || "#94a3b8" }}
                />
                <span className="truncate font-medium">
                  {cal.summary || cal.id}
                </span>
                {cal.primary && (
                  <span className="ml-auto rounded bg-brand-primary/10 px-2 py-0.5 text-xs uppercase tracking-wide text-brand-primary dark:bg-brand-primary/20 dark:text-blue-300">
                    Primary
                  </span>
                )}
                {isLinked && (
                  <span className="ml-auto text-xs uppercase tracking-wide text-slate-400">
                    Linked
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function ConfigureStep({
  picked,
  calendarType,
  setCalendarType,
  color,
  setColor,
  defaultVoteLevel,
  setDefaultVoteLevel,
  advanced,
  setAdvanced,
  error,
  submitting,
  onBack,
  onConfirm,
}) {
  return (
    <div className="space-y-5 py-2 text-sm">
      <p className="rounded-xl bg-slate-50 px-3 py-2 dark:bg-slate-800">
        Linking <strong>{picked.summary || picked.id}</strong>
      </p>

      <fieldset>
        <legend className="font-medium">How should it affect your availability?</legend>
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          <label className="flex cursor-pointer items-start gap-2 rounded-xl border border-slate-200 p-3 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800">
            <input
              type="radio"
              name="calendarType"
              value="conflict"
              checked={calendarType === "conflict"}
              onChange={() => setCalendarType("conflict")}
              className="mt-1"
            />
            <span>
              <span className="block font-medium">Conflict</span>
              <span className="text-slate-500 dark:text-slate-400">
                Events block time. Auto-NO on overlapping slots.
              </span>
            </span>
          </label>
          <label className="flex cursor-pointer items-start gap-2 rounded-xl border border-slate-200 p-3 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-800">
            <input
              type="radio"
              name="calendarType"
              value="free"
              checked={calendarType === "free"}
              onChange={() => setCalendarType("free")}
              className="mt-1"
            />
            <span>
              <span className="block font-medium">Free time</span>
              <span className="text-slate-500 dark:text-slate-400">
                Events are positive availability signals.
              </span>
            </span>
          </label>
        </div>
      </fieldset>

      {calendarType === "free" && (
        <>
          <fieldset>
            <legend className="font-medium">Default vote level for matching slots</legend>
            <div className="mt-2 flex gap-2">
              {["FEASIBLE", "PREFERRED"].map((level) => (
                <label
                  key={level}
                  className="flex cursor-pointer items-center gap-2 rounded-xl border border-slate-200 px-3 py-2 dark:border-slate-700"
                >
                  <input
                    type="radio"
                    name="defaultVoteLevel"
                    value={level}
                    checked={defaultVoteLevel === level}
                    onChange={() => setDefaultVoteLevel(level)}
                  />
                  <span className="text-sm">{level}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <AdvancedFieldsSection
            value={advanced}
            defaults={{
              leewayFrontMinutes: 0,
              leewayBackMinutes: 0,
              fitMode: "full-duration",
              appliesToQuestingGroupIds: null,
            }}
            onChange={setAdvanced}
          />
        </>
      )}

      <fieldset>
        <legend className="font-medium">Color</legend>
        <div className="mt-2 flex flex-wrap gap-2">
          {LINKED_CALENDAR_PALETTE.map((c) => (
            <button
              type="button"
              key={c}
              onClick={() => setColor(c)}
              aria-label={`Pick color ${c}`}
              aria-pressed={color === c}
              className={`h-7 w-7 rounded-full ring-offset-2 transition-shadow ${
                color === c ? "ring-2 ring-ring" : ""
              }`}
              style={{ backgroundColor: c }}
            />
          ))}
        </div>
      </fieldset>

      {error && (
        <p className="rounded-xl bg-rose-100 px-3 py-2 text-sm text-rose-700 dark:bg-rose-900/30 dark:text-rose-300">
          {error}
        </p>
      )}

      <DialogFooter>
        <button
          type="button"
          onClick={onBack}
          disabled={submitting}
          className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
        >
          Back
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={submitting}
          className="inline-flex items-center gap-2 rounded-xl bg-brand-primary px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-primary/90 disabled:opacity-60"
        >
          {submitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          Confirm
        </button>
      </DialogFooter>
    </div>
  );
}
